import {Food} from '../food/food';
import {Snake, SnakeDir} from '../snake/snake';
import {Brick} from '../brick/brick';
import {ILevel, ITeleport, Level} from '../levels/level';
import {HttpClient} from '@angular/common/http';
import {Options} from '../options/options';
import {Subject} from 'rxjs';
import {Levels, TStartPos} from '../../verliba-shared';

export class Board {
  cols = 10;
  rows = 10;
  timer: any;
  delay = 500;
  startLevel = 0;

  snakes: Snake[] = [];
  food: Food[] = [];
  walls: Brick[][] = [];

  levels = new Levels<Level, ILevel>(Level);
  level: Level;
  over = false;
  paused = false;
  events = new Subject<string>();
  turns = new Subject<SnakeDir>();
  counter = 0;
  goal = 1;
  waiting = false;
  multi = false;

  constructor(private httpClient: HttpClient, private options: Options) {
    this.levels.load(httpClient).catch();
  }

  async setLevel(next: boolean = false): Promise<void> {
    try {
      await this.levels.load(this.httpClient);
      const level = next ? this.levels.next : this.levels.current;
      if (!level){ return; }
      console.log(level.level);
      await level.load(this.httpClient);
      this.level = level;
      this.cols = level.width;
      this.rows = level.height;
      this.goal = level.info.food;
      this.food = [];
      const walls = this.newWalls();

      for (let y = 0 ; y < this.rows; y ++) {
        for (let x = 0 ; x < this.cols; x ++) {
          const shift = level.info.shift || {x: 0, y: 0};
          const pixel = level.pixel((x + shift.x) % this.cols, (y + shift.y) % this.rows);
          const [r, g, b, a] = pixel;
          if (a) {
            const brick = walls[y][x];
            brick.empty = false;
            brick.type = 'wall';
            brick.color = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          }
        }
      }

      const start = this.level.info.start
        || {x: Math.floor(this.cols / 2), y: 3, dir: 'r'};
      this.snakes = [
        new Snake(start.x, start.y, start.dir as SnakeDir),
      ];
      this.snakes[0].grow = this.level.info.grow;
      this.snakes[0].tooltip = 'me';
      for (const snake of this.snakes) {
        snake.ate = 0;
      }

      this.walls = walls;

      (this.level.info.ports || []).forEach(port => {
        const brick = walls[port.y][port.x];
        brick.empty = false;
        brick.type = 'port';
        brick.port = port;
      });
      this.events.next('level');
    } catch (err) {
      console.error(err);
    }
  }

  newWalls(): Brick[][] {
    return new Array(this.rows).fill(0)
      .map(() => new Array(this.cols).fill(0)
        .map(() => new Brick()));
  }

  async restart(): Promise<void> {
    this.pause();
    this.delay = this.options.delay;
    this.over = false;

    this.events.next('restart');
    this.levels.reset(this.startLevel);
    await this.setLevel();
    if (!this.waiting) {
      for (const snake of this.snakes) {
        snake.score = 0;
        snake.move();
      }
    }
    if (!this.waiting) {
      this.countDown(3);
    }
  }

  countDown(count: number): void {
    this.counter = count;
    if (!count) {
      this.setSpeed();
    } else {
      setTimeout(() => this.countDown(count - 1), 1000);
    }
  }

  pause(silent = true): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
      if (!silent) {
        this.paused = true;
      }
    }
  }

  resume(): void {
    if (!this.timer) {
      this.paused = false;
      this.countDown(3);
    }
  }

  setSpeed(factor = 1): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.timer = setInterval(() => this.move(), this.delay / factor);
  }

  gameOver(snake: Snake): void {
    snake.lives --;
    if (snake === this.snakes[0]) {
      this.events.next('died');
    }
    const index = this.snakes.indexOf(snake);
    if (index >= 0) {
      this.snakes.splice(index, 1);
    }
    if (!snake.lives) {
      if (!this.snakes.length) {
        clearInterval(this.timer);
        this.over = true;
        this.events.next('over');
      }
    } else if (!this.multi) {
      this.pause();
      this.setLevel()
        .then(() => this.resume());
    }
  }

  infiniteField(snake: Snake): void {
    if (snake.x < 0) {
      snake.x = this.cols - 1;
    }
    if (snake.x >= this.cols) {
      snake.x = 0;
    }
    if (snake.y < 0) {
      snake.y = this.rows - 1;
    }
    if (snake.y >= this.rows) {
      snake.y = 0;
    }
  }

  biteWall(snake: Snake): boolean {
    const brick = this.walls[snake.y][snake.x];
    if (!brick.empty) {
      switch (brick.type) {
        case 'wall':
          this.gameOver(snake);
          return true;
        case 'port':
          this.teleport(brick.port, snake);
          break;
      }
    }
    return false;
  }

  move(): void {
    for (const snake of this.snakes) {
      if (!this.moveSnake(snake)) {
        return;
      }
      this.events.next('move');
      if (this.multi) {
        break;
      }
    }
  }

  eat(snake: Snake): void {
    const newFood = [];
    let ate: Food = null;
    for (const f of this.food) {
      if (f.expiration > 0) {
        f.expiration --;
      }
      if (f.expiration) {
        if (!ate && f.x !== snake.mX || f.y !== snake.mY) {
          newFood.push(f);
        } else {
          ate = f;
        }
      }
    }
    this.food = newFood;

    if (!this.food.length) {
      this.putFood();
    }

    if (!ate) { return; }
    switch (ate.type) {
      case 'normal':
        this.grow(snake);
        break;
      case 'double':
        this.grow(snake, 2);
        break;
      case 'shrink':
        this.grow(snake, -1);
        break;
      case 'speed':
        this.setSpeed(1.5);
        snake.score += this.level.info.grow * 3;
        break;
      case 'slow':
        this.setSpeed(1 / 1.5);
        snake.score += this.level.info.grow;
        break;
      case 'life':
        console.log('add life', snake.lives);
        snake.lives ++;
        this.events.next('life');
        break;
      case 'hunger':
        for (let i = 0; i < 10; i ++) {
          this.putFood();
        }
        this.goal += 10;
        this.goal = Math.min(this.goal, this.level.info.food * 2);
        this.food.forEach(f => f.expiration *= 2);
        break;
    }
    // console.log(this.snake.grow, this.snake.length);
    snake.ate ++;
    this.events.next('ate');
    if (snake.ate === this.goal && snake === this.snakes[0]) {
      this.events.next('complete');
      this.pause();
      if (this.snakes[0] === snake) {
        this.setLevel(!this.levels.isLast)
          .then(() => this.resume());
      }
    }
  }

  teleport(from: ITeleport, snake: Snake): void {
    const destinations = this.level.info.ports.filter((port, index) => {
      if (port === from ) { return false; }
      if (from.group && port.group === from.group ) {
        return true;
      }
      if (from.dst !== undefined) {
        return index === from.dst;
      }
      return !from.group;
    });
    const dest = destinations[Math.floor(Math.random() * destinations.length)];
    snake.x = dest.x;
    snake.y = dest.y;
    if (from.reverse) {
      snake.reverse();
    }
    this.events.next('teleport');
  }

  containsNothing(wx: number, wy: number): boolean {
    if (!this.walls[wy][wx].empty) { return false; }
    for (const snake of this.snakes) {
      for (const bit of snake.bits) {
        if (bit.x === wx && bit.y === wy) { return false; }
      }
    }
    for (const food of this.food) {
      if (food.x === wx && food.y === wy) { return false; }
    }
    return true;
  }

  putFood(): Food | undefined {
    const dx = Math.floor(Math.random() * this.cols);
    const dy = Math.floor(Math.random() * this.rows);
    for (let y = 0; y < this.rows; y ++) {
      const wy = (y + dy) % this.rows;
      for (let x = 0; x < this.rows; x ++) {
        const wx = (x + dx) % this.cols;
        if (this.containsNothing(wx, wy)) {
          const add = new Food( wx, wy, this.level.foodType);
          if (this.level.info.expiration !== undefined) {
            add.expiration = this.level.info.expiration;
          }
          this.food.push(add);
          this.events.next('food');
          return add;
        }
      }
    }
  }

  handleKey(key: string): boolean {
    if (!this.timer && key !== 'Enter' && key !== ' ') { return true; }
    const dir = this.snakes[0].direction;
    let newDir = dir;
    switch (key) {
      case 'ArrowLeft':
        if (dir !== 'r') { newDir = 'l'; } break;
      case 'ArrowRight':
        if (dir !== 'l') { newDir = 'r'; } break;
      case 'ArrowDown':
        if (dir !== 't') { newDir = 'b'; } break;
      case 'ArrowUp':
        if (dir !== 'b') { newDir = 't'; } break;
      case ' ': this.paused ? this.resume() : this.pause(false); break;
      case 'Enter': this.restart().catch(); break;
      default: return true;
    }
    if (newDir !== dir) {
      this.turnSnake(this.snakes[0], newDir);
    }
    return false;
  }

  turnSnake(snake: Snake, dir): void {
    snake.direction = dir;
    snake.skipMove = false;
    this.moveSnake(snake);
    snake.skipMove = true;
    setTimeout(() => snake.skipMove = false, this.delay * 0.5);
    if (snake === this.snakes[0]) {
      this.turns.next(dir);
    }
  }

  private biteSnakes(snake: Snake): boolean {
    for (const other of this.snakes) {
      for (const bit of other.bits) {
        if (snake.x === bit.x && snake.y === bit.y) {
          this.gameOver(snake);
          return true;
        }
      }
    }
  }

  addSnake(snake?: Snake, atStart = false): Snake {
    if (!snake) {
      snake = this.newSnake();
    }
    if (atStart) {
      this.snakes.splice(0, 0, snake);
    } else {
      this.snakes.push(snake);
    }
    return snake;
  }

  newSnake(): Snake {
    const pos = this.findPlaceForSnake(4);
    const snake = new Snake(pos.x, pos.y, pos.dir as SnakeDir);
    snake.grow = this.level.info.grow;
    return snake;
  }

  private findPlaceForSnake(places: number): TStartPos {
    const sx = Math.round(Math.random() * this.cols);
    const sy = Math.round(Math.random() * this.cols);
    for (let dx = 0; dx < this.cols; dx ++){
      const x = (sx + dx) % this.cols;
      for (let dy = 0; dy < this.cols; dy ++) {
        const y = (sy + dy) % this.rows;
        let isOK = true;
        for (let len = 0; len < places; len ++) {
          if (!this.containsNothing((x + len) % this.cols, y)) {
            isOK = false;
            break;
          }
        }
        if (isOK) {
          return {x, y, dir: 'r'};
        }
      }
    }
  }

  moveSnake(snake: Snake): boolean {
    if (snake.skipMove) {
      snake.skipMove = false;
      return;
    }
    if (!snake.preMove()) {
      this.gameOver(snake);
      return;
    }
    this.infiniteField(snake);
    if (this.biteWall(snake)) { return false; }
    if (this.biteSnakes(snake)) { return false; }
    snake.move(false);
    this.eat(snake);
    return true;
  }

  private grow(snake: Snake, factor = 1): void {
    snake.grow += this.level.info.grow * factor;
    snake.score += Math.abs(this.level.info.grow * factor);
    if (snake === this.snakes[0]) {
      this.events.next('grow');
    }
  }

  deleteSnake(snake: Snake): void {
    const index = this.snakes.indexOf(snake);
    if (index >= 0) {
      this.snakes.splice(index, 1);
    }
  }
}
